import React, { useEffect, useState } from "react";
import Axios from "../../../axios/axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import imageCompression from "browser-image-compression";
import SupplierNavbar from "../Admin/SuppplierNavbar";
import SupplierSidebar from "../Admin/SupplierSidebar";

function EngEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Main part state
  const [part, setPart] = useState({
    partName: "",
    partNo: "",
    brandId: "",
    categoryId: "",
    modelId: "",
    regionId: "",
    year: "",
    productType: "",
    pricePerUnit: "",
    manufactureDate: "",
    warrantyPeriod: "",
    material: "",
    catalogue: { title: "", url: "" }, // Changed to single object
    compatibleModels: [],
    features: [],
    certifications: [],
    images: [],
    description: "",
    manufacturer: "",
    supplier: {
      supplierId: "",
      name: "",
      location: "",
      contactNumber: "",
      email: "",
    },
  });

  // Supporting data states
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [models, setModels] = useState([]);
  const [regions, setRegions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [supplier, setSupplier] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [manufacturers, setManufacturers] = useState([]);

  useEffect(() => {
    const storedSupplier = sessionStorage.getItem("supplierInfo");
    if (storedSupplier) {
      const supplierInfo = JSON.parse(storedSupplier);
      setSupplier(supplierInfo);
    }
  }, []);

  // Input values for array fields
  const [inputValues, setInputValues] = useState({
    compatibleModels: "",
    features: "",
    certifications: "",
  });

  // Error states
  const [errors, setErrors] = useState({
    compatibleModels: "",
    features: "",
    certifications: "",
  });

  // Fetch data on component mount
  useEffect(() => {
    fetchPartDetails();
    fetchSupportingData();
  }, [id]);

  // Fetch all supporting data
  const fetchSupportingData = async () => {
    try {
      const [brandsRes, regionsRes, suppliersRes, manufacturersRes] =
        await Promise.all([
          Axios.get("/type-based-details"),
          Axios.get("/regions"),
          Axios.get("/supplier"),
          Axios.get("/manufacturer"),
        ]);

      setBrands(brandsRes.data.engineering?.brands || []);
      setCategories(brandsRes.data.engineering?.categories || []);
      setModels(brandsRes.data.engineering?.models || []);
      setRegions(regionsRes.data || []);
      setSuppliers(suppliersRes.data.data || []);
      setManufacturers(manufacturersRes.data || []);
    } catch (error) {
      console.error("Error fetching supporting data:", error);
      toast.error("Failed to load supporting data");
    }
  };

  // Fetch part details
  const fetchPartDetails = async () => {
    try {
      const response = await Axios.get(`/engineeringpart/get/${id}`);
      const data = response.data;

      setPart({
        partName: data.partName || "",
        partNo: data.partNo || "",
        brandId: data.brandId?._id || "",
        categoryId: data.categoryId?._id || "",
        modelId: data.modelId?._id || "",
        regionId: data.regionId?._id || "",
        year: data.year || "",
        productType: data.productType || "",
        pricePerUnit: data.pricePerUnit || "",
        manufactureDate: data.manufactureDate || "",
        warrantyPeriod: data.warrantyPeriod || "",
        material: data.material || "",
        catalogue: data.catalogue || { title: "", url: "" }, // Ensure single object
        compatibleModels: data.compatibleModels || [],
        features: data.features || [],
        certifications: data.certifications || [],
        images: data.images || [],
        description: data.description || "",
        manufacturer: data.manufacturer?._id || "",

        supplier: data.supplier || {
          supplierId: "",
          name: "",
          location: "",
          contactNumber: "",
          email: "",
        },
      });

      setLoading(false);
    } catch (error) {
      console.error("Error fetching part details:", error);
      toast.error("Failed to load part details");
      setLoading(false);
    }
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "supplierId") {
      const selectedSupplier = suppliers.find((sup) => sup._id === value);
      setPart((prev) => ({
        ...prev,
        supplier: {
          supplierId: value,
          name: selectedSupplier?.supplierName || "",
          location: selectedSupplier?.address || "",
          contactNumber: selectedSupplier?.phoneNumber || "",
          email: selectedSupplier?.emailAddress || "",
        },
      }));
    } else {
      setPart((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle image upload
  const uploadFileHandler = async (e, type) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);

    try {
      const uploadedFiles = await Promise.all(
        Array.from(files).map(async (file) => {
          const compressedFile = await imageCompression(file, {
            maxSizeMB: 0.2,
            maxWidthOrHeight: 800,
          });

          const formData = new FormData();
          formData.append("images", compressedFile);

          const { data } = await Axios.post("/upload-multiple", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });

          return {
            name: file.name,
            url: data.fileUrls[0],
            public_id: data.public_id,
          };
        })
      );

      if (type === "image") {
        setPart((prev) => ({
          ...prev,
          images: [...prev.images, ...uploadedFiles],
        }));
      } else if (type === "catalogue") {
        // For catalogue, we'll only take the first file
        setPart((prev) => ({
          ...prev,
          catalogue: {
            title: uploadedFiles[0].name,
            url: uploadedFiles[0].url,
          },
        }));
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      toast.error("Failed to upload files");
    } finally {
      setUploading(false);
    }
  };

  // Remove image
  const removeImage = async (index, public_id) => {
    try {
      if (public_id) {
        await Axios.delete(`/engineeringpart/delete-image/${id}`, {
          data: { public_id },
        });
      }

      setPart((prev) => {
        const updatedImages = [...prev.images];
        updatedImages.splice(index, 1);
        return { ...prev, images: updatedImages };
      });

      toast.success("Image removed successfully");
    } catch (error) {
      console.error("Error removing image:", error);
      toast.error("Failed to remove image");
    }
  };

  // Remove catalogue file
  const removeCatalogue = async () => {
    try {
      if (part.catalogue.public_id) {
        await Axios.delete(`/engineeringpart/delete-pdf/${id}`, {
          data: { public_id: part.catalogue.public_id },
        });
      }

      setPart((prev) => ({
        ...prev,
        catalogue: { title: "", url: "" },
      }));

      toast.success("Catalogue removed successfully");
    } catch (error) {
      console.error("Error removing catalogue:", error);
      toast.error("Failed to remove catalogue");
    }
  };

  // Handle adding items to arrays (compatibleModels, features, certifications)
  const handleAddItem = (key) => {
    if (!inputValues[key]?.trim()) {
      setErrors((prev) => ({ ...prev, [key]: "Enter something!" }));
      return;
    }

    setPart((prev) => ({
      ...prev,
      [key]: [...prev[key], inputValues[key]],
    }));

    setInputValues((prev) => ({ ...prev, [key]: "" }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  // Handle removing items from arrays
  const handleRemoveItem = (key, index) => {
    setPart((prev) => {
      const updatedArray = [...prev[key]];
      updatedArray.splice(index, 1);
      return { ...prev, [key]: updatedArray };
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await Axios.put(`/engineeringpart/update/${id}`, part);

      toast.success("Engineering part updated successfully!");
      navigate("/SupplierProducts");
    } catch (error) {
      console.error("Error updating part:", error);
      toast.error("Failed to update part");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="enhanced-body">
      <SupplierNavbar
        supplier={supplier}
        setSupplier={setSupplier}
        toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
      />

      <div className="container-fluid">
        <div className="row">
          {sidebarOpen && (
            <div className="col-md-2">
              <SupplierSidebar />
            </div>
          )}

          <div className={sidebarOpen ? "col-md-10" : "col-md-12"}>
            <div className="row">
              <div className="col-12 grid-margin stretch-card">
                <div
                  className="card"
                  style={{
                    maxHeight: "95vh",
                    overflowY: "auto",
                    minHeight: "90vh",
                  }}
                >
                  <div className="card-body">
                    <h4 className="card-title">Edit Engineering Part</h4>
                    <form className="forms-sample" onSubmit={handleSubmit}>
                      {/* Part Name */}
                      <div className="form-group">
                        <label>Part Name</label>
                        <input
                          type="text"
                          name="partName"
                          value={part.partName}
                          onChange={handleChange}
                          className="form-control"
                          required
                        />
                      </div>

                      {/* Part No */}
                      <div className="form-group">
                        <label>Part No</label>
                        <input
                          type="text"
                          name="partNo"
                          value={part.partNo}
                          onChange={handleChange}
                          className="form-control"
                          required
                        />
                      </div>
                      {/* Description */}
                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          name="description"
                          value={part.description}
                          onChange={handleChange}
                          className="form-control"
                          rows="3"
                          required
                        ></textarea>
                      </div>

                      {/* Brand */}
                      <div className="form-group">
                        <label>Brand</label>
                        <select
                          name="brandId"
                          value={part.brandId}
                          onChange={handleChange}
                          className="form-control"
                          required
                        >
                          <option value="">Select Brand</option>
                          {brands.map((brand) => (
                            <option key={brand._id} value={brand._id}>
                              {brand.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Model */}
                      <div className="form-group">
                        <label>Model</label>
                        <select
                          name="modelId"
                          value={part.modelId}
                          onChange={handleChange}
                          className="form-control"
                          required
                        >
                          <option value="">Select Model</option>
                          {models.map((model) => (
                            <option key={model._id} value={model._id}>
                              {model.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Category */}
                      <div className="form-group">
                        <label>Category</label>
                        <select
                          name="categoryId"
                          value={part.categoryId}
                          onChange={handleChange}
                          className="form-control"
                          required
                        >
                          <option value="">Select Category</option>
                          {categories.map((category) => (
                            <option key={category._id} value={category._id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Region */}
                      <div className="form-group">
                        <label>Region</label>
                        <select
                          name="regionId"
                          value={part.regionId}
                          onChange={handleChange}
                          className="form-control"
                          required
                        >
                          <option value="">Select Region</option>
                          {regions.map((region) => (
                            <option key={region._id} value={region._id}>
                              {region.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Year */}
                      <div className="form-group">
                        <label>Year</label>
                        <input
                          type="number"
                          name="year"
                          value={part.year}
                          onChange={handleChange}
                          className="form-control"
                          required
                        />
                      </div>

                      {/* Product Type */}
                      <div className="form-group">
                        <label>Product Type</label>
                        <select
                          name="productType"
                          value={part.productType}
                          onChange={handleChange}
                          className="form-control"
                          required
                        >
                          <option value="">Select Product Type</option>
                          <option value="Engineering">Engineering</option>
                          <option value="Building">Building</option>
                          <option value="Auto-Spare">Auto-Spare</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Manufacturer</label>
                        <select
                          className="form-control"
                          name="manufacturer"
                          value={part.manufacturer}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select Manufacturer</option>
                          {manufacturers.map((m) => (
                            <option key={m._id} value={m._id}>
                              {m.manufacturerName}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Price Per Unit */}
                      <div className="form-group">
                        <label>Price Per Unit</label>
                        <input
                          type="number"
                          name="pricePerUnit"
                          value={part.pricePerUnit}
                          onChange={handleChange}
                          className="form-control"
                          required
                        />
                      </div>

                      {/* Manufacture Date */}
                      <div className="form-group">
                        <label>Manufacture Date</label>
                        <input
                          type="date"
                          name="manufactureDate"
                          value={part.manufactureDate}
                          onChange={handleChange}
                          className="form-control"
                          required
                        />
                      </div>

                      {/* Warranty Period */}
                      <div className="form-group">
                        <label>Warranty Period</label>
                        <input
                          type="text"
                          name="warrantyPeriod"
                          value={part.warrantyPeriod}
                          onChange={handleChange}
                          className="form-control"
                          required
                        />
                      </div>

                      {/* Material */}
                      <div className="form-group">
                        <label>Material</label>
                        <input
                          type="text"
                          name="material"
                          value={part.material}
                          onChange={handleChange}
                          className="form-control"
                          required
                        />
                      </div>

                      {/* Compatible Models */}
                      <div className="form-group">
                        <label>Compatible Models</label>
                        <div className="d-flex">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Add compatible model"
                            value={inputValues.compatibleModels}
                            onChange={(e) =>
                              setInputValues({
                                ...inputValues,
                                compatibleModels: e.target.value,
                              })
                            }
                          />
                          <button
                            type="button"
                            className="btn btn-primary mx-2"
                            onClick={() => handleAddItem("compatibleModels")}
                          >
                            Add
                          </button>
                        </div>
                        {errors.compatibleModels && (
                          <small className="text-danger">
                            {errors.compatibleModels}
                          </small>
                        )}
                        <ul className="mt-2">
                          {part.compatibleModels.map((model, index) => (
                            <li
                              key={index}
                              className="d-flex align-items-center"
                            >
                              {model}
                              <button
                                type="button"
                                className="btn btn-danger btn-sm ml-2"
                                onClick={() =>
                                  handleRemoveItem("compatibleModels", index)
                                }
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Features */}
                      <div className="form-group">
                        <label>Features</label>
                        <div className="d-flex">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Add feature"
                            value={inputValues.features}
                            onChange={(e) =>
                              setInputValues({
                                ...inputValues,
                                features: e.target.value,
                              })
                            }
                          />
                          <button
                            type="button"
                            className="btn btn-primary mx-2"
                            onClick={() => handleAddItem("features")}
                          >
                            Add
                          </button>
                        </div>
                        {errors.features && (
                          <small className="text-danger">
                            {errors.features}
                          </small>
                        )}
                        <ul className="mt-2">
                          {part.features.map((feature, index) => (
                            <li
                              key={index}
                              className="d-flex align-items-center"
                            >
                              {feature}
                              <button
                                type="button"
                                className="btn btn-danger btn-sm ml-2"
                                onClick={() =>
                                  handleRemoveItem("features", index)
                                }
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Certifications */}
                      <div className="form-group">
                        <label>Certifications</label>
                        <div className="d-flex">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Add certification"
                            value={inputValues.certifications}
                            onChange={(e) =>
                              setInputValues({
                                ...inputValues,
                                certifications: e.target.value,
                              })
                            }
                          />
                          <button
                            type="button"
                            className="btn btn-primary mx-2"
                            onClick={() => handleAddItem("certifications")}
                          >
                            Add
                          </button>
                        </div>
                        {errors.certifications && (
                          <small className="text-danger">
                            {errors.certifications}
                          </small>
                        )}
                        <ul className="mt-2">
                          {part.certifications.map((certification, index) => (
                            <li
                              key={index}
                              className="d-flex align-items-center"
                            >
                              {certification}
                              <button
                                type="button"
                                className="btn btn-danger btn-sm ml-2"
                                onClick={() =>
                                  handleRemoveItem("certifications", index)
                                }
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Images */}
                      <div className="form-group">
                        <label>Images</label>
                        <div className="d-flex align-items-center mb-2">
                          <input
                            type="file"
                            id="image-upload"
                            className="d-none"
                            multiple
                            onChange={(e) => uploadFileHandler(e, "image")}
                            accept="image/*"
                          />
                          <label
                            htmlFor="image-upload"
                            className="btn btn-primary btn-sm mb-0"
                          >
                            <i className="icon-plus"></i> Add Images
                          </label>
                        </div>
                        {uploading && (
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="sr-only">Uploading...</span>
                          </div>
                        )}
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {part.images.map((img, index) => (
                            <div key={index} className="position-relative">
                              <img
                                src={img.url}
                                alt={img.name}
                                height="80"
                                className="rounded border"
                              />
                              <button
                                type="button"
                                className="btn btn-danger btn-xs position-absolute top-0 end-0"
                                onClick={() =>
                                  removeImage(index, img.public_id)
                                }
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Catalogue */}
                      <div className="form-group">
                        <label>Catalogue</label>
                        <input
                          type="file"
                          className="form-control"
                          onChange={(e) => uploadFileHandler(e, "catalogue")}
                          accept=".pdf,.jpg,.jpeg,.png"
                        />
                        {uploading ? (
                          <div className="spinner-border" role="status">
                            <span className="sr-only">Uploading...</span>
                          </div>
                        ) : (
                          part.catalogue?.url && (
                            <div className="mt-2">
                              <a
                                href={part.catalogue.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="d-block"
                              >
                                {part.catalogue.title || "View Catalogue"}
                              </a>
                              <button
                                type="button"
                                className="btn btn-danger btn-sm mt-2"
                                onClick={removeCatalogue}
                              >
                                Remove Catalogue
                              </button>
                            </div>
                          )
                        )}
                      </div>

                      {/* Supplier */}
                      <div className="form-group">
                        <label>Supplier</label>
                        <select
                          name="supplierId"
                          value={part.supplier.supplierId}
                          onChange={handleChange}
                          className="form-control"
                          required
                        >
                          <option value="">Select Supplier</option>
                          {suppliers.map((supplier) => (
                            <option key={supplier._id} value={supplier._id}>
                              {supplier.supplierName}
                            </option>
                          ))}
                        </select>
                        <div className="mt-2">
                          <input
                            type="text"
                            className="form-control"
                            value={part.supplier.name}
                            placeholder="Supplier Name"
                            readOnly
                          />
                          <input
                            type="text"
                            className="form-control mt-2"
                            value={part.supplier.location}
                            placeholder="Location"
                            readOnly
                          />
                          <input
                            type="text"
                            className="form-control mt-2"
                            value={part.supplier.contactNumber}
                            placeholder="Contact Number"
                            readOnly
                          />
                          <input
                            type="email"
                            className="form-control mt-2"
                            value={part.supplier.email}
                            placeholder="Email"
                            readOnly
                          />
                        </div>
                      </div>

                      {/* Buttons */}
                      <div className="mt-4">
                        <button
                          type="submit"
                          className="btn btn-primary mr-2"
                          disabled={loading}
                        >
                          {loading ? (
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </span>
                          ) : (
                            "Update"
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-light"
                          onClick={() => navigate("/SupplierProducts")}
                        >
                          Cancel
                        </button>
                      </div>
                      <div style={{ marginBottom: "60px" }}></div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EngEdit;
